// src/pages/monitoring/issues/IssueForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  X, AlertTriangle, Building2, Network,
  Save, ArrowLeft, CheckCircle
} from 'lucide-react';
import issueService from '../../../services/issueService';
import projectService from '../../../services/projectService';
import foSegmentService from '../../../services/foSegmentService';
import towerSiteService from '../../../services/towerSiteService';
// ✅ FIX: Import 2 new methods — replaces the non-existent getMilestonesByTarget
import {
  getLeafMilestonesBySegment,
  getLeafMilestonesBySite
} from '../../../services/milestoneService';

// ── Category Card ─────────────────────────────────────────────────────

const CategoryCard = ({ cat, selected, onClick }) => (
  <button type="button" onClick={() => onClick(cat.value)}
    className={`flex flex-col items-center gap-1.5 p-2.5 rounded-lg border-2 transition-all text-center ${
      selected
        ? 'border-red-500 bg-red-50 shadow-md'
        : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
    }`}>
    <span className="text-xl">{cat.icon}</span>
    <span className={`text-xs font-semibold leading-tight ${
      selected ? 'text-red-700' : 'text-gray-700'
    }`}>
      {cat.displayName}
    </span>
  </button>
);

// ── Static category fallback ──────────────────────────────────────────
const STATIC_CATEGORIES = [
  { value: 'MATERIAL',             displayName: 'Material',          icon: '🏗️' },
  { value: 'CUACA',                displayName: 'Cuaca',             icon: '🌧️' },
  { value: 'AKSES',                displayName: 'Akses',             icon: '🚧' },
  { value: 'TEKNIS',               displayName: 'Teknis',            icon: '⚙️' },
  { value: 'TENAGA_KERJA',         displayName: 'Tenaga Kerja',      icon: '👷' },
  { value: 'KESELAMATAN',          displayName: 'Keselamatan (K3)',  icon: '🛡️' },
  { value: 'COMMUNITY_CASE',       displayName: 'Community Case',    icon: '🤝' },
  { value: 'PERIZINAN',            displayName: 'Perizinan',         icon: '📋' },
  { value: 'VENDOR_SUBKON',        displayName: 'Vendor & Subkon',   icon: '🏢' },
  { value: 'ANGGARAN',             displayName: 'Anggaran',          icon: '💰' },
  { value: 'JADWAL',               displayName: 'Jadwal',            icon: '📅' },
  { value: 'CUSTOMER_KONTRAK',     displayName: 'Customer & Kontrak',icon: '🤝' },
  { value: 'DOKUMEN_ADMINISTRASI', displayName: 'Dokumen & Admin',   icon: '📄' },
  { value: 'LAINNYA',              displayName: 'Lainnya',           icon: '🔧' },
];

// ── Main Component ────────────────────────────────────────────────────

const IssueForm = ({ onSuccess, onCancel }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title:       '',
    description: '',
    category:    '',
    priority:    'MEDIUM',
    issueScope:  'SITE_LEVEL',
    projectId:   '',
    foSegmentId: '',
    towerSiteId: '',
    milestoneId: '',
  });

  const [isSubmitting, setIsSubmitting]           = useState(false);
  const [errors, setErrors]                       = useState({});
  const [categories, setCategories]               = useState(STATIC_CATEGORIES);
  const [projects, setProjects]                   = useState([]);
  const [segments, setSegments]                   = useState([]);
  const [sites, setSites]                         = useState([]);
  const [milestones, setMilestones]               = useState([]);
  const [selectedProject, setSelectedProject]     = useState(null);
  const [loadingMilestones, setLoadingMilestones] = useState(false);

  // ── Load categories & projects ─────────────────────────────────────
  useEffect(() => {
    issueService.getCategories()
      .then((data) => { if (data?.length) setCategories(data); })
      .catch(() => {});

    projectService.getAllProjects()
      .then((data) => setProjects(Array.isArray(data) ? data : (data.content ?? [])))
      .catch(() => toast.error('Gagal memuat daftar project'));
  }, []);

  // ── Load segments / sites when project changes ─────────────────────
  useEffect(() => {
    if (!formData.projectId) {
      setSegments([]); setSites([]); setMilestones([]); setSelectedProject(null);
      return;
    }
    const proj = projects.find((p) => String(p.id) === String(formData.projectId));
    setSelectedProject(proj);
    setFormData((prev) => ({ ...prev, foSegmentId: '', towerSiteId: '', milestoneId: '' }));
    setMilestones([]);

    if (proj?.projectType === 'FO') {
      foSegmentService.getFOSegmentsByProject(formData.projectId)
        .then((data) => setSegments(Array.isArray(data) ? data : (data.content ?? [])))
        .catch(() => {});
      setSites([]);
    } else if (proj?.projectType === 'TOWER') {
      towerSiteService.getTowerSitesByProject(formData.projectId)
        .then((data) => setSites(Array.isArray(data) ? data : (data.content ?? [])))
        .catch(() => {});
      setSegments([]);
    }
  }, [formData.projectId, projects]);

  // ── Load LEAF milestones when target changes ───────────────────────
  // ✅ FIX: Calls correct API endpoints via getLeafMilestonesBySegment / getLeafMilestonesBySite
  //         Old code called getMilestonesByTarget() which did not exist.
  useEffect(() => {
    if (formData.issueScope !== 'MILESTONE_LEVEL') {
      setMilestones([]);
      setFormData((prev) => ({ ...prev, milestoneId: '' }));
      return;
    }
    const segId = formData.foSegmentId;
    const siteId = formData.towerSiteId;
    if (!segId && !siteId) { setMilestones([]); return; }

    setLoadingMilestones(true);
    setFormData((prev) => ({ ...prev, milestoneId: '' }));

    const loader = segId
      ? getLeafMilestonesBySegment(segId)   // GET /api/segments/{id}/milestones → filter isGroup=false
      : getLeafMilestonesBySite(siteId);    // GET /api/sites/{id}/milestones → filter isGroup=false

    loader
      .then(setMilestones)
      .catch(() => { toast.error('Gagal memuat milestone'); setMilestones([]); })
      .finally(() => setLoadingMilestones(false));
  }, [formData.issueScope, formData.foSegmentId, formData.towerSiteId]);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleScopeChange = (scope) => {
    setFormData((prev) => ({
      ...prev,
      issueScope:  scope,
      milestoneId: scope !== 'MILESTONE_LEVEL' ? '' : prev.milestoneId,
      ...(scope === 'PROJECT_LEVEL'
        ? { foSegmentId: '', towerSiteId: '', milestoneId: '' }
        : {}),
    }));
  };

  const handleProjectChange = (value) => {
    setFormData((prev) => ({
      ...prev, projectId: value, foSegmentId: '', towerSiteId: '', milestoneId: '',
    }));
    if (errors.projectId) setErrors((prev) => ({ ...prev, projectId: null }));
  };

  const handleTargetChange = (field, value) => {
    const other = field === 'foSegmentId' ? 'towerSiteId' : 'foSegmentId';
    setFormData((prev) => ({ ...prev, [field]: value, [other]: '', milestoneId: '' }));
    if (errors.target) setErrors((prev) => ({ ...prev, target: null }));
  };

  // ── Validation ────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!formData.title.trim())       errs.title       = 'Judul issue wajib diisi';
    if (!formData.description.trim()) errs.description = 'Deskripsi wajib diisi';
    if (!formData.category)           errs.category    = 'Kategori wajib dipilih';
    if (!formData.priority)           errs.priority    = 'Priority wajib dipilih';
    if (!formData.issueScope)         errs.issueScope  = 'Scope wajib dipilih';
    if (!formData.projectId)          errs.projectId   = 'Project wajib dipilih';
    if (formData.issueScope === 'MILESTONE_LEVEL') {
      if (!formData.foSegmentId && !formData.towerSiteId)
        errs.target = 'Pilih segment atau site untuk scope MILESTONE_LEVEL';
      if (!formData.milestoneId)
        errs.milestoneId = 'Milestone wajib dipilih untuk scope MILESTONE_LEVEL';
    }
    if (formData.issueScope === 'SITE_LEVEL') {
      if (!formData.foSegmentId && !formData.towerSiteId)
        errs.target = 'Pilih segment atau site untuk scope SITE_LEVEL';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) { toast.error('Mohon lengkapi semua field yang wajib diisi'); return; }
    setIsSubmitting(true);
    try {
      const payload = {
        title:       formData.title.trim(),
        description: formData.description.trim(),
        category:    formData.category,
        priority:    formData.priority,
        issueScope:  formData.issueScope,
        projectId:   Number(formData.projectId),
        foSegmentId: formData.foSegmentId  ? Number(formData.foSegmentId)  : null,
        towerSiteId: formData.towerSiteId  ? Number(formData.towerSiteId)  : null,
        milestoneId: formData.milestoneId  ? Number(formData.milestoneId)  : null,
      };
      const created = await issueService.createIssue(payload);
      toast.success(`✅ Issue ${created.issueId} berhasil dibuat!`);
      if (onSuccess) onSuccess(created); else navigate(-1);
    } catch { toast.error('Gagal membuat issue. Silakan coba lagi.'); }
    finally  { setIsSubmitting(false); }
  };

  const isFO        = selectedProject?.projectType === 'FO';
  const needsTarget = formData.issueScope !== 'PROJECT_LEVEL';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onCancel ?? (() => navigate(-1))}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Buat Issue Baru</h1>
              <p className="text-gray-500 text-sm mt-0.5">
                Laporkan kendala atau issue terkait project
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onCancel ?? (() => navigate(-1))}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium text-sm flex items-center gap-2">
              <X className="w-4 h-4" /> Batal
            </button>
            <button onClick={handleSubmit} disabled={isSubmitting}
              className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm flex items-center gap-2 shadow-md disabled:opacity-60">
              <Save className="w-4 h-4" />
              {isSubmitting ? 'Menyimpan...' : 'Simpan Issue'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — Steps 1..4 */}
        <div className="lg:col-span-2 space-y-6">

          {/* Step 1 — Scope */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
              Scope Issue
            </h2>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'MILESTONE_LEVEL', label: 'Milestone Level',        desc: 'Issue pada milestone spesifik' },
                { value: 'SITE_LEVEL',      label: 'Site / Segment Level',   desc: 'Issue pada satu site/segment' },
                { value: 'PROJECT_LEVEL',   label: 'Project Level',          desc: 'Issue berdampak seluruh project' },
              ].map((opt) => (
                <button key={opt.value} type="button" onClick={() => handleScopeChange(opt.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    formData.issueScope === opt.value
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-red-300'
                  }`}>
                  <div className={`text-sm font-semibold ${
                    formData.issueScope === opt.value ? 'text-red-700' : 'text-gray-800'
                  }`}>{opt.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2 — Target */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
              Target Issue
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Project <span className="text-red-500">*</span>
                </label>
                <select value={formData.projectId}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.projectId ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`}>
                  <option value="">-- Pilih Project --</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      [{p.projectType}] {p.projectCode} — {p.projectName}
                    </option>
                  ))}
                </select>
                {errors.projectId && <p className="text-xs text-red-600 mt-1">{errors.projectId}</p>}
              </div>

              {needsTarget && selectedProject && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {isFO ? 'FO Segment' : 'Tower Site'}
                    <span className="text-red-500"> *</span>
                  </label>
                  <select
                    value={isFO ? formData.foSegmentId : formData.towerSiteId}
                    onChange={(e) =>
                      handleTargetChange(isFO ? 'foSegmentId' : 'towerSiteId', e.target.value)
                    }
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.target ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`}>
                    <option value="">-- Pilih {isFO ? 'FO Segment' : 'Tower Site'} --</option>
                    {(isFO ? segments : sites).map((item) => (
                      <option key={item.id} value={item.id}>
                        {isFO
                          ? `${item.spanId} — ${item.siteNameAB}`
                          : `${item.siteId} — ${item.siteName}`}
                      </option>
                    ))}
                  </select>
                  {errors.target && <p className="text-xs text-red-600 mt-1">{errors.target}</p>}
                </div>
              )}

              {formData.issueScope === 'MILESTONE_LEVEL'
                && (formData.foSegmentId || formData.towerSiteId) && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Milestone <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-400 font-normal ml-1">
                      (hanya milestone pekerjaan aktual)
                    </span>
                  </label>
                  <select value={formData.milestoneId}
                    onChange={(e) => handleChange('milestoneId', e.target.value)}
                    disabled={loadingMilestones}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 ${
                      errors.milestoneId ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`}>
                    <option value="">
                      {loadingMilestones
                        ? '⏳ Memuat milestone...'
                        : `-- Pilih Milestone (${milestones.length} tersedia) --`}
                    </option>
                    {milestones.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.itemCode} — {m.milestoneName}
                        {m.finalProgress != null
                          ? ` (${Number(m.finalProgress).toFixed(0)}%)`
                          : ''}
                      </option>
                    ))}
                  </select>
                  {errors.milestoneId && (
                    <p className="text-xs text-red-600 mt-1">{errors.milestoneId}</p>
                  )}
                  {!loadingMilestones && milestones.length === 0
                    && (formData.foSegmentId || formData.towerSiteId) && (
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠️ Tidak ada milestone tersedia. Pastikan milestone sudah di-setup
                      di menu Project Planning.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Step 3 — Category */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
              Kategori Issue <span className="text-red-500 text-sm font-normal">*</span>
            </h2>
            {errors.category && (
              <p className="text-xs text-red-600 mb-3 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {errors.category}
              </p>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5">
              {categories.map((cat) => (
                <CategoryCard key={cat.value} cat={cat}
                  selected={formData.category === cat.value}
                  onClick={(v) => handleChange('category', v)} />
              ))}
            </div>
          </div>

          {/* Step 4 — Description */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
              Deskripsi Issue
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Judul Issue <span className="text-red-500">*</span>
                </label>
                <input type="text" value={formData.title} maxLength={255}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Contoh: Material kabel belum datang dari supplier"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.title ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`} />
                {errors.title
                  ? <p className="text-xs text-red-600 mt-1">{errors.title}</p>
                  : <p className="text-xs text-gray-400 mt-1">{formData.title.length}/255</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Deskripsi Detail <span className="text-red-500">*</span>
                </label>
                <textarea rows={5} value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Jelaskan detail issue, lokasi, dampak terhadap progress, dan saran penanganan..."
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-y ${
                    errors.description ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`} />
                {errors.description && (
                  <p className="text-xs text-red-600 mt-1">{errors.description}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right — Priority + Info */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-base font-bold text-gray-800 mb-4">Priority Level</h2>
            <div className="space-y-2">
              {[
                { value: 'LOW',      label: 'Low',      desc: 'Tidak menghambat pekerjaan',
                  dot: 'bg-green-500',  ring: 'border-green-400 bg-green-50 text-green-700' },
                { value: 'MEDIUM',   label: 'Medium',   desc: 'Perlu perhatian segera',
                  dot: 'bg-yellow-500', ring: 'border-yellow-400 bg-yellow-50 text-yellow-700' },
                { value: 'HIGH',     label: 'High',     desc: 'Menghambat pekerjaan',
                  dot: 'bg-red-500',    ring: 'border-red-400 bg-red-50 text-red-700' },
                { value: 'CRITICAL', label: 'Critical', desc: 'Stop work!',
                  dot: 'bg-purple-600', ring: 'border-purple-400 bg-purple-50 text-purple-700' },
              ].map((opt) => (
                <button key={opt.value} type="button"
                  onClick={() => handleChange('priority', opt.value)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                    formData.priority === opt.value ? opt.ring : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${opt.dot}`} />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{opt.label}</div>
                    <div className="text-xs opacity-70">{opt.desc}</div>
                  </div>
                  {formData.priority === opt.value && (
                    <CheckCircle className="w-4 h-4 flex-shrink-0 opacity-70" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Info</p>
                <p className="text-xs leading-relaxed">
                  Issue akan masuk ke dashboard Issues Tracking dan dapat di-assign ke
                  tim terkait oleh PM atau Project Control.
                </p>
              </div>
            </div>
          </div>

          {selectedProject && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-bold text-blue-900 mb-2">Project Terpilih</h3>
              <div className="flex items-center gap-2 mb-1.5">
                {selectedProject.projectType === 'TOWER'
                  ? <Building2 className="w-4 h-4 text-blue-600" />
                  : <Network className="w-4 h-4 text-blue-600" />}
                <span className="text-xs font-semibold text-blue-800">
                  {selectedProject.projectType}
                </span>
              </div>
              <p className="text-xs font-bold text-gray-900">{selectedProject.projectCode}</p>
              <p className="text-xs text-gray-600 mt-0.5">{selectedProject.projectName}</p>
            </div>
          )}

          <button onClick={handleSubmit} disabled={isSubmitting}
            className="w-full px-5 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-bold text-sm flex items-center justify-center gap-2 shadow-md disabled:opacity-60 transition-all">
            <Save className="w-5 h-5" />
            {isSubmitting ? 'Menyimpan Issue...' : 'Simpan Issue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IssueForm;